import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Camera, Plus, Edit, Trash2, MapPin, Monitor, Search,
  Power, AlertTriangle, CheckCircle, RefreshCw, Eye, Wifi, WifiOff,
  Crosshair, Settings, Layers, FolderPlus, Users, UserMinus
} from 'lucide-react';
import { 
  fetchAdminCameras, 
  createAdminCamera, 
  updateAdminCamera, 
  deleteAdminCamera,
  updateCameraStatus,
  CameraData,
  API_BASE_URL,
  GO2RTC_URL,
  fetchCameraPermissions,
  addCameraPermission,
  removeCameraPermission,
  fetchAdminClients,
  type CameraPermissionItem,
  type AdminClientItem
} from '@/lib/authFetch';
import { toast } from 'sonner';
import { useLiveStreamName } from '@/hooks/useLiveStreamName';

type CaptureZonePreview = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  rule: string;
};

type TripwirePreview = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

function parseCaptureZonePreview(raw?: string | null): CaptureZonePreview | null {
  if (!raw || !String(raw).trim()) return null;
  try {
    const z = JSON.parse(String(raw));
    const clamp = (v: unknown) => Math.max(0, Math.min(1, Number(v)));
    const x1 = clamp(z?.x1);
    const y1 = clamp(z?.y1);
    const x2 = clamp(z?.x2);
    const y2 = clamp(z?.y2);
    if (![x1, y1, x2, y2].every((n) => Number.isFinite(n))) return null;
    return {
      x1: Math.min(x1, x2),
      y1: Math.min(y1, y2),
      x2: Math.max(x1, x2),
      y2: Math.max(y1, y2),
      rule: String(z?.rule || 'bottom_center'),
    };
  } catch {
    return null;
  }
}

function parseTripwirePreview(raw?: string | null): TripwirePreview | null {
  if (!raw || !String(raw).trim()) return null;
  try {
    const z = JSON.parse(String(raw));
    if (z?.enabled === false) return null;
    const clamp = (v: unknown) => Math.max(0, Math.min(1, Number(v)));
    const x1 = clamp(z?.x1);
    const y1 = clamp(z?.y1);
    const x2 = clamp(z?.x2);
    const y2 = clamp(z?.y2);
    if (![x1, y1, x2, y2].every((n) => Number.isFinite(n))) return null;
    return { x1, y1, x2, y2 };
  } catch {
    return null;
  }
}

// Interface para grupos
interface GrupoCamera {
  id: number;
  nome: string;
  descricao?: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cor?: string;
  tipo: string;
  total_cameras: number;
  cameras_online: number;
}


const CameraManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [grupoFilter, setGrupoFilter] = useState<number | 'all' | 'sem_grupo'>('all');
  const [cameras, setCameras] = useState<CameraData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState<CameraData | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cameraToDelete, setCameraToDelete] = useState<CameraData | null>(null);
  const [saving, setSaving] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Estados para grupos
  const [grupos, setGrupos] = useState<GrupoCamera[]>([]);
  const [grupoMode, setGrupoMode] = useState<'sem_grupo' | 'existente' | 'novo'>('sem_grupo');
  const [selectedGrupoId, setSelectedGrupoId] = useState<number | null>(null);
  const [novoGrupo, setNovoGrupo] = useState({
    nome: '',
    endereco: '',
    cidade: '',
    estado: ''
  });
  
  // Estado para modal de visualização ao vivo
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingCamera, setViewingCamera] = useState<CameraData | null>(null);
  const liveStreamName = useLiveStreamName(
    viewModalOpen && viewingCamera ? (viewingCamera.stream_id || `camera_${viewingCamera.id}`) : null
  );

  // Permissões: quais clientes podem visualizar uma câmera
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [permissionsCamera, setPermissionsCamera] = useState<CameraData | null>(null);
  const [permissionsList, setPermissionsList] = useState<CameraPermissionItem[]>([]);
  const [clientsList, setClientsList] = useState<AdminClientItem[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Estados para busca de localização
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [enderecoEncontrado, setEnderecoEncontrado] = useState<boolean | null>(null);
  const [coordenadasPreview, setCoordenadasPreview] = useState<{lat: number, lon: number, display: string} | null>(null);
  const [mostrarCoordsManual, setMostrarCoordsManual] = useState(false);

  // Form state - Atualizado para API Intelbras
  const [formData, setFormData] = useState<CameraData>({
    nome: '',
    localizacao: '',
    ip: '',
    porta: 80,
    rtsp_porta: 554,
    porta_web: undefined,
    usuario: 'admin',
    senha: '',
    canal: 1,
    status: 'offline',
    tipo: '',
    fabricante: 'intelbras',
    modelo: '',
    resolucao: '',
    ptz_suportado: false,
    observacao: '',
    latitude: undefined,
    longitude: undefined,
    serial_p2p: '',
    stream_id: '',
    deteccao_zona_json: '',
    deteccao_linha_json: '',
    face_monitor_enabled: false,
    finalidade: '',
    grupo_id: undefined,
    posicao_no_grupo: ''
  });

  // Carregar câmeras e grupos
  useEffect(() => {
    loadCameras();
    loadGrupos();
  }, []);

  const loadCameras = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error('Sessão admin expirada. Faça login novamente.');
        setCameras([]);
        return;
      }
      const data = await fetchAdminCameras();
      setCameras(Array.isArray(data) ? data : []);
    } catch (error) {
      const msg =
        error instanceof Error && error.name === 'AbortError'
          ? 'API demorou demais — backend pode estar sobrecarregado com OCR'
          : error instanceof Error
            ? error.message
            : 'Erro ao carregar câmeras';
      toast.error(msg);
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGrupos = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/admin/grupos-cameras`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGrupos(data);
      }
    } catch {
      // Erro ao carregar grupos
    }
  };

  // Buscar coordenadas por endereço usando backend (Google Maps + Nominatim fallback)
  const buscarCoordenadas = async (endereco: string) => {
    if (!endereco || endereco.trim().length < 5) {
      setEnderecoEncontrado(null);
      setCoordenadasPreview(null);
      return;
    }

    setBuscandoEndereco(true);
    setEnderecoEncontrado(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/geocode?address=${encodeURIComponent(endereco)}`
      );
      const data = await response.json();

      if (data.success && data.latitude && data.longitude) {
        const lat = data.latitude;
        const lon = data.longitude;
        const displayName = data.formatted_address || endereco;
        
        setCoordenadasPreview({ lat, lon, display: displayName });
        setEnderecoEncontrado(true);
        
        // Atualiza o formData com as coordenadas
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lon
        }));
      } else {
        setEnderecoEncontrado(false);
        setCoordenadasPreview(null);
      }
    } catch {
      setEnderecoEncontrado(false);
      setCoordenadasPreview(null);
    } finally {
      setBuscandoEndereco(false);
    }
  };

  // Debounce para busca de endereço
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.localizacao && formData.localizacao.trim().length >= 5) {
        buscarCoordenadas(formData.localizacao);
      }
    }, 1000); // Aguarda 1 segundo após parar de digitar

    return () => clearTimeout(timer);
  }, [formData.localizacao]);

  // Criar novo grupo
  const criarNovoGrupo = async (): Promise<number | null> => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Buscar coordenadas usando backend se tem endereço
      let latitude, longitude;
      if (novoGrupo.endereco) {
        try {
          const enderecoCompleto = `${novoGrupo.endereco}, ${novoGrupo.cidade || ''} ${novoGrupo.estado || ''}`;
          const geoResponse = await fetch(
            `${API_BASE_URL}/geocode?address=${encodeURIComponent(enderecoCompleto)}`
          );
          const geoData = await geoResponse.json();
          if (geoData.success && geoData.latitude && geoData.longitude) {
            latitude = geoData.latitude;
            longitude = geoData.longitude;
          }
        } catch {
          // Erro ao buscar coordenadas
        }
      }

      const response = await fetch(`${API_BASE_URL}/admin/grupos-cameras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...novoGrupo,
          latitude,
          longitude,
          status: 'ativo'
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Grupo "${novoGrupo.nome}" criado!`);
        loadGrupos();
        return data.id;
      } else {
        toast.error('Erro ao criar grupo');
        return null;
      }
    } catch (error) {
      toast.error('Erro ao criar grupo');
      return null;
    }
  };

  // Buscar nome do grupo pela ID
  const getGrupoNome = (grupoId: number | undefined) => {
    if (!grupoId) return null;
    const grupo = grupos.find(g => g.id === grupoId);
    return grupo ? grupo.nome : null;
  };

  // Buscar cor do grupo pela ID
  const getGrupoCor = (grupoId: number | undefined) => {
    if (!grupoId) return null;
    const grupo = grupos.find(g => g.id === grupoId);
    return grupo ? grupo.cor : null;
  };

  const handleOpenModal = (camera?: CameraData) => {
    // Resetar estados de localização
    setEnderecoEncontrado(null);
    setCoordenadasPreview(null);
    setMostrarCoordsManual(false);
    setBuscandoEndereco(false);
    
    if (camera) {
      setEditingCamera(camera);
      setFormData({
        nome: camera.nome || '',
        localizacao: camera.localizacao || '',
        ip: camera.ip || camera.ip_address || '',
        porta: camera.porta || 80,
        rtsp_porta: camera.rtsp_porta || 554,
        porta_web: camera.porta_web,
        usuario: camera.usuario || 'admin',
        senha: camera.senha || '',
        canal: camera.canal || 1,
        status: camera.status || 'offline',
        tipo: camera.tipo || '',
        fabricante: camera.fabricante || 'intelbras',
        modelo: camera.modelo || '',
        resolucao: camera.resolucao || '',
        ptz_suportado: camera.ptz_suportado || false,
        observacao: camera.observacao || '',
        latitude: camera.latitude,
        longitude: camera.longitude,
        serial_p2p: camera.serial_p2p || '',
        stream_id: camera.stream_id || '',
        deteccao_zona_json: camera.deteccao_zona_json || '',
        deteccao_linha_json: camera.deteccao_linha_json || '',
        face_monitor_enabled: camera.face_monitor_enabled ?? false,
        finalidade:
          camera.finalidade === 'captura' || camera.finalidade === 'visualizacao'
            ? camera.finalidade
            : '',
        grupo_id: camera.grupo_id,
        posicao_no_grupo: camera.posicao_no_grupo || ''
      });
      
      // Se já tem coordenadas, mostrar preview
      if (camera.latitude && camera.longitude) {
        setCoordenadasPreview({
          lat: Number(camera.latitude),
          lon: Number(camera.longitude),
          display: camera.localizacao || 'Coordenadas salvas'
        });
        setEnderecoEncontrado(true);
      }
      
      // Configurar estado do grupo
      if (camera.grupo_id) {
        setGrupoMode('existente');
        setSelectedGrupoId(camera.grupo_id);
      } else {
        setGrupoMode('sem_grupo');
        setSelectedGrupoId(null);
      }
    } else {
      setEditingCamera(null);
      setFormData({
        nome: '',
        localizacao: '',
        ip: '',
        porta: 80,
        rtsp_porta: 554,
        porta_web: undefined,
        usuario: 'admin',
        senha: '',
        canal: 1,
        status: 'offline',
        tipo: '',
        fabricante: 'intelbras',
        modelo: '',
        resolucao: '',
        ptz_suportado: false,
        observacao: '',
        latitude: undefined,
        longitude: undefined,
        serial_p2p: '',
        stream_id: '',
        deteccao_zona_json: '',
        deteccao_linha_json: '',
        face_monitor_enabled: false,
        finalidade: '',
        grupo_id: undefined,
        posicao_no_grupo: ''
      });
      // Reset estados do grupo
      setGrupoMode('sem_grupo');
      setSelectedGrupoId(null);
      setNovoGrupo({
        nome: '',
        endereco: '',
        cidade: '',
        estado: ''
      });
    }
    setModalOpen(true);
  };

  // Testar conexão com a câmera antes de salvar
  const handleTestConnection = async () => {
    if (!formData.ip) {
      toast.error('Informe o IP da câmera');
      return;
    }

    setTestingConnection(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cameras/testar-conexao`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ip: formData.ip,
          porta: formData.porta || 80,
          usuario: formData.usuario || 'admin',
          senha: formData.senha || ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ Conexão OK: ${data.message}`);
        
        // Preencher informações do dispositivo se disponível
        if (data.device_info) {
          setFormData(prev => ({
            ...prev,
            modelo: data.device_info.deviceType || prev.modelo,
            firmware: data.device_info.softwareVersion || prev.firmware
          }));
        }
      } else {
        toast.error(`❌ Falha: ${data.message}`);
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nome) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (formData.finalidade !== 'captura' && formData.finalidade !== 'visualizacao') {
      toast.error('Selecione o tipo de uso da câmera');
      return;
    }
    if (!formData.ip && !formData.serial_p2p && !formData.stream_id) {
      toast.error('Informe o IP, Serial P2P ou Stream ID');
      return;
    }
    
    // Validar grupo se modo "novo"
    if (grupoMode === 'novo' && !novoGrupo.nome) {
      toast.error('Informe o nome do grupo');
      return;
    }

    setSaving(true);
    try {
      // Se for criar novo grupo, criar primeiro
      let grupo_id = grupoMode === 'existente' ? selectedGrupoId : undefined;
      
      if (grupoMode === 'novo') {
        const novoGrupoId = await criarNovoGrupo();
        if (!novoGrupoId) {
          setSaving(false);
          return;
        }
        grupo_id = novoGrupoId;
      }

      // Usar coordenadas já encontradas (pelo debounce) ou as manuais
      const latitude = formData.latitude;
      const longitude = formData.longitude;

      // Stream ID: normalizar para coincidir com path RTMP (ex: totem_sj_3, sem espaços)
      const streamIdNorm = (formData.stream_id || '').trim().replace(/\s+/g, '_') || undefined;

      if ((formData.ip || '').trim() && !(formData.senha || '').trim()) {
        toast.error('Informe a senha da câmera (acesso web).');
        setSaving(false);
        return;
      }

      // Preparar dados para envio
      const dataToSend = {
        ...formData,
        stream_id: streamIdNorm || formData.stream_id,
        latitude,
        longitude,
        ip_address: formData.ip,
        grupo_id: grupo_id || null,
        // Monitoramento facial sempre ativo
        face_monitor_enabled: true,
        finalidade: formData.finalidade === 'captura' ? 'captura' : 'visualizacao',
      };

      if (editingCamera?.id) {
        await updateAdminCamera(editingCamera.id, dataToSend);
        toast.success('Câmera atualizada!');
      } else {
        await createAdminCamera(dataToSend);
        toast.success('Câmera criada!');
      }
      
      // Feedback sobre localização
      if (formData.localizacao && formData.localizacao.trim() !== '' && !latitude && !longitude) {
        toast.warning('Câmera salva, mas sem coordenadas para o mapa. Edite e ajuste o endereço.');
      } else if (latitude && longitude) {
        toast.info('📍 Localização salva no mapa!');
      }
      
      setModalOpen(false);
      loadCameras();
      loadGrupos();
    } catch {
      toast.error('Erro ao salvar câmera');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!cameraToDelete?.id) return;

    try {
      await deleteAdminCamera(cameraToDelete.id);
      toast.success('Câmera deletada com sucesso!');
      setDeleteModalOpen(false);
      setCameraToDelete(null);
      loadCameras();
    } catch (error) {
      toast.error('Erro ao deletar câmera');
    }
  };

  const handleToggleStatus = async (camera: CameraData) => {
    if (!camera.id) return;
    const newStatus = camera.status === 'online' ? 'offline' : 'online';
    try {
      await updateCameraStatus(camera.id, newStatus);
      toast.success(`Câmera ${newStatus === 'online' ? 'ativada' : 'desativada'}`);
      loadCameras();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // Verificar status de todas as câmeras via API Intelbras
  const handleVerificarTodas = async () => {
    setVerificando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cameras/status-geral`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      toast.success(`Verificação: ${data.online} online, ${data.offline} offline`);
      loadCameras();
    } catch (error) {
      toast.error('Erro ao verificar câmeras');
    } finally {
      setVerificando(false);
    }
  };

  // Abrir modal de permissões (quem pode ver a câmera)
  const handleOpenPermissions = async (camera: CameraData) => {
    if (!camera.id) return;
    if (!localStorage.getItem('admin_token')) {
      toast.error('Sessão de admin não encontrada. Faça login novamente na área admin.');
      return;
    }
    setPermissionsCamera(camera);
    setPermissionsModalOpen(true);
    setPermissionsList([]);
    setClientsList([]);
    setSelectedClientId('');
    setLoadingPermissions(true);
    try {
      const [permRes, clientsRes] = await Promise.all([
        fetchCameraPermissions(camera.id),
        fetchAdminClients()
      ]);
      setPermissionsList(permRes.permissions ?? []);
      setClientsList(Array.isArray(clientsRes) ? clientsRes : []);
    } catch (e: any) {
      const msg = e?.message || e?.detail || 'Erro ao carregar permissões';
      toast.error(msg);
      setPermissionsModalOpen(false);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleAddPermission = async () => {
    if (!permissionsCamera?.id || !selectedClientId) return;
    try {
      await addCameraPermission(permissionsCamera.id, Number(selectedClientId));
      const res = await fetchCameraPermissions(permissionsCamera.id);
      setPermissionsList(res.permissions);
      setSelectedClientId('');
      toast.success('Cliente autorizado a visualizar esta câmera');
    } catch (e: any) {
      toast.error(e.message || 'Erro ao adicionar permissão');
    }
  };

  const handleRemovePermission = async (userId: number) => {
    if (!permissionsCamera?.id) return;
    try {
      await removeCameraPermission(permissionsCamera.id, userId);
      const res = await fetchCameraPermissions(permissionsCamera.id);
      setPermissionsList(res.permissions);
      toast.success('Permissão removida');
    } catch {
      toast.error('Erro ao remover permissão');
    }
  };

  // Visualizar câmera ao vivo
  const handleVisualizarCamera = async (camera: CameraData) => {
    let cameraForView = camera;
    try {
      const token = localStorage.getItem('admin_token');
      if (token && camera.id) {
        const resp = await fetch(`${API_BASE_URL}/admin/cameras/${camera.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          cameraForView = (await resp.json()) as CameraData;
        }
      }
    } catch {
      // fallback silencioso para os dados já carregados na lista
    }

    setViewingCamera(cameraForView);
    setViewModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Online</Badge>;
      case 'offline':
        return <Badge className="bg-red-500/20 text-red-400"><AlertTriangle className="w-3 h-3 mr-1" />Offline</Badge>;
      case 'manutencao':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><RefreshCw className="w-3 h-3 mr-1" />Manutenção</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">Desconhecido</Badge>;
    }
  };

  const filteredCameras = cameras.filter(camera => {
    // Filtro por texto
    const grupoNome = getGrupoNome(camera.grupo_id);
    const matchesSearch = 
      camera.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      camera.localizacao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      camera.ip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      camera.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grupoNome?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro por status
    const matchesStatus = 
      statusFilter === 'all' || 
      camera.status === statusFilter;
    
    // Filtro por grupo
    const matchesGrupo = 
      grupoFilter === 'all' ||
      (grupoFilter === 'sem_grupo' && !camera.grupo_id) ||
      (typeof grupoFilter === 'number' && camera.grupo_id === grupoFilter);
    
    return matchesSearch && matchesStatus && matchesGrupo;
  });
  const previewZone = parseCaptureZonePreview(viewingCamera?.deteccao_zona_json);
  const previewLine = parseTripwirePreview(viewingCamera?.deteccao_linha_json);

  return (
    <div className="space-y-6">
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Gerenciamento de Câmeras
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:mb-6">
            <div className="flex gap-2 flex-1 flex-wrap">
              <div className="relative flex-1 min-w-[150px]">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar câmeras..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-protec-dark border-protec-gold/30 text-white text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'online' | 'offline')}
                className="bg-protec-dark border border-protec-gold/30 text-white rounded-md px-2 sm:px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-protec-gold/50"
              >
                <option value="all">Todas</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
              <select
                value={grupoFilter === 'all' ? 'all' : grupoFilter === 'sem_grupo' ? 'sem_grupo' : grupoFilter.toString()}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'all') setGrupoFilter('all');
                  else if (val === 'sem_grupo') setGrupoFilter('sem_grupo');
                  else setGrupoFilter(parseInt(val));
                }}
                className="bg-protec-dark border border-protec-gold/30 text-white rounded-md px-2 sm:px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-protec-gold/50"
              >
                <option value="all">Todos os grupos</option>
                <option value="sem_grupo">Sem grupo</option>
                {grupos.map(g => (
                  <option key={g.id} value={g.id}>{g.nome}</option>
                ))}
              </select>
            </div>
            <Button 
              className="bg-protec-gold text-black hover:bg-protec-gold-light text-sm"
              onClick={() => handleOpenModal()}
            >
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              Nova Câmera
            </Button>
          </div>

          {/* Stats rápidos */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center">
              <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold mx-auto mb-1 sm:mb-2" />
              <p className="text-base sm:text-lg font-bold text-white">{cameras.length}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Total</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center">
              <Wifi className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-base sm:text-lg font-bold text-white">{cameras.filter(c => c.status === 'online').length}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Online</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center">
              <WifiOff className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-base sm:text-lg font-bold text-white">{cameras.filter(c => c.status === 'offline').length}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Offline</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Carregando câmeras...
            </div>
          ) : filteredCameras.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>
                {statusFilter === 'offline' 
                  ? 'Nenhuma câmera offline' 
                  : statusFilter === 'online'
                    ? 'Nenhuma câmera online'
                    : 'Nenhuma câmera cadastrada'}
              </p>
              {statusFilter === 'all' && (
                <Button 
                  className="mt-4 bg-protec-gold text-black"
                  onClick={() => handleOpenModal()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Cadastrar primeira câmera
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {filteredCameras.map((camera) => (
                <div key={camera.id} className="border border-protec-gold/20 rounded-lg p-3 sm:p-4 bg-protec-dark/50 hover:border-protec-gold/40 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Icon + Info */}
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${camera.status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        <Camera className={`w-5 h-5 sm:w-6 sm:h-6 ${camera.status === 'online' ? 'text-green-400' : 'text-red-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold text-sm sm:text-lg truncate pb-0.5 min-h-[1.25em]" style={{ paddingBottom: '2px' }} title={camera.nome}>{camera.nome}</h3>
                          {camera.grupo_id && (
                            <span 
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium"
                              style={{ 
                                backgroundColor: `${getGrupoCor(camera.grupo_id)}20`,
                                color: getGrupoCor(camera.grupo_id) || '#9CA3AF'
                              }}
                            >
                              <Layers className="w-2.5 h-2.5" />
                              {getGrupoNome(camera.grupo_id)}
                            </span>
                          )}
                        </div>
                        {(camera.ip || camera.ip_address) && (
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400 mt-1">
                            <span>IP: {camera.ip || camera.ip_address}</span>
                            <span>Porta: {camera.porta || 80}</span>
                            <span>Canal: {camera.canal || 1}</span>
                          </div>
                        )}
                        {camera.stream_id && (
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400 mt-1">
                            <span className="font-mono pb-0.5 min-h-[1.25em]" style={{ paddingBottom: '2px' }} title={camera.stream_id}>Stream ID: {camera.stream_id}</span>
                            <span>Canal: {camera.canal ?? 1}</span>
                          </div>
                        )}
                        <p className="text-gray-500 text-xs sm:text-sm flex items-center gap-1 mt-1 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{camera.localizacao || 'Localização não informada'}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Status + Actions */}
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 mt-2 sm:mt-0">
                      <div className="text-left sm:text-right">
                        {getStatusBadge(camera.status || 'offline')}
                        <div className="mt-1">
                          <Badge className={
                            camera.finalidade === 'captura'
                              ? 'bg-orange-500/20 text-orange-300 border-0'
                              : 'bg-sky-500/20 text-sky-300 border-0'
                          }>
                            {camera.finalidade === 'captura' ? 'Captura' : 'Visualização'}
                          </Badge>
                        </div>
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-1 hidden sm:block">
                          {camera.tipo && `${camera.tipo}`}
                          {camera.resolucao && ` • ${camera.resolucao}`}
                          {camera.ptz_suportado && ' • PTZ'}
                        </p>
                      </div>
                      <div className="flex gap-1 sm:gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => handleVisualizarCamera(camera)}
                          title="Visualizar ao vivo"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className={`border-0 h-8 w-8 p-0 ${camera.status === 'online' ? 'bg-green-600/20 text-green-400 hover:bg-green-600/40' : 'bg-gray-600/20 text-gray-400 hover:bg-gray-600/40'}`}
                          onClick={() => handleToggleStatus(camera)}
                          title={camera.status === 'online' ? 'Desativar' : 'Ativar'}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => handleOpenPermissions(camera)}
                          title="Quem pode ver esta câmera"
                          disabled={camera.finalidade === 'captura'}
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-amber-600/20 text-amber-400 hover:bg-amber-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => handleOpenModal(camera)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-rose-600/20 text-rose-400 hover:bg-rose-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => { setCameraToDelete(camera); setDeleteModalOpen(true); }}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criar/Editar Câmera - Com opção de Grupo */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera className="w-5 h-5 text-protec-gold" />
              {editingCamera ? 'Editar Câmera' : 'Nova Câmera'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Nome da Câmera */}
            <div>
              <Label>Nome da Câmera <span className="text-red-400">*</span></Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Câmera Entrada Principal"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1 pb-0.5 min-h-[2rem]"
                style={{ paddingBottom: '4px' }}
              />
            </div>

            {/* Stream ID (RTMP Push) */}
            <div className="min-w-0 flex-1 w-full">
              <Label className="flex items-center gap-2">
                Stream ID (RTMP Push)
              </Label>
              <Input
                value={formData.stream_id || ''}
                onChange={(e) => setFormData({...formData, stream_id: e.target.value})}
                onBlur={(e) => {
                  const v = (e.target.value || '').trim().replace(/\s+/g, '_');
                  if (v !== (formData.stream_id || '').trim()) setFormData(prev => ({ ...prev, stream_id: v }));
                }}
                placeholder="Deixe vazio para IP/P2P (preenchido auto: camera_+id)"
                className="bg-protec-dark border-protec-gold/30 text-white mt-2 font-mono w-full min-w-[240px] overflow-visible pb-0.5"
                style={{ paddingBottom: '4px' }}
              />
            </div>

            <div>
              <Label className="text-sm text-gray-300">
                Tipo de uso <span className="text-red-400">*</span>
              </Label>
              <select
                required
                value={
                  formData.finalidade === 'captura' || formData.finalidade === 'visualizacao'
                    ? formData.finalidade
                    : ''
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    finalidade: e.target.value === 'captura' || e.target.value === 'visualizacao'
                      ? e.target.value
                      : '',
                  })
                }
                className="mt-1 w-full appearance-none rounded-md border border-protec-gold/30 bg-protec-dark text-white text-sm px-2.5 py-1.5 pr-8 focus:outline-none focus:ring-1 focus:ring-protec-gold"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23D4AF37' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.6rem center',
                  backgroundSize: '0.85rem',
                }}
              >
                <option value="" disabled>
                  Selecione…
                </option>
                <option value="visualizacao">Visualização</option>
                <option value="captura">Captura</option>
              </select>
            </div>

            {/* IP da Câmera - Só mostra se não tiver Stream ID */}
            {!formData.stream_id && (
              <div>
                <Label>IP da Câmera {!formData.stream_id && <span className="text-red-400">*</span>}</Label>
                <Input
                  value={formData.ip}
                  onChange={async (e) => {
                    const ip = e.target.value;
                    setFormData({...formData, ip});
                    
                    // Tenta buscar localização pelo IP quando tiver um IP válido
                    if (ip && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) {
                      try {
                        const response = await fetch(`http://ip-api.com/json/${ip}?fields=status,lat,lon,city,regionName,country`);
                        const data = await response.json();
                        if (data.status === 'success') {
                          setFormData(prev => ({
                            ...prev,
                            ip,
                            latitude: data.lat,
                            longitude: data.lon,
                            localizacao: `${data.city}, ${data.regionName} - ${data.country}`
                          }));
                        }
                      } catch (e) {
                        // Ignora erro silenciosamente
                      }
                    }
                  }}
                  placeholder="192.168.1.108"
                  className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                />
              </div>
            )}

            {/* Canal - Sempre visível */}
            <div>
              <Label>Canal</Label>
              <Input
                type="number"
                value={formData.canal || 1}
                onChange={(e) => setFormData({...formData, canal: parseInt(e.target.value) || 1})}
                placeholder="1"
                min={1}
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            {/* Porta/credenciais: necessárias para IP (OCR / acesso à câmera).
                Stream ID sozinho NÃO deve esconder — o sistema preenche camera_{id} automaticamente. */}
            {(!!formData.ip || !formData.stream_id) && (
              <>
                <div>
                  <Label>Porta HTTP</Label>
                  <Input
                    type="number"
                    value={formData.porta || 80}
                    onChange={(e) => setFormData({...formData, porta: parseInt(e.target.value) || 80})}
                    placeholder="80"
                    className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Usuário <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.usuario}
                      onChange={(e) => setFormData({...formData, usuario: e.target.value})}
                      placeholder="admin"
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label>Senha <span className="text-red-400">*</span></Label>
                    <Input
                      type="password"
                      value={formData.senha}
                      onChange={(e) => setFormData({...formData, senha: e.target.value})}
                      placeholder="senha da câmera"
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Endereço da Câmera */}
            <div>
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-protec-gold" />
                Endereço (para o mapa)
              </Label>
              <div className="relative mt-1">
                <Input
                  value={formData.localizacao}
                  onChange={(e) => setFormData({...formData, localizacao: e.target.value})}
                  placeholder="Rua, Nº, Cidade, Estado"
                  className="bg-protec-dark border-protec-gold/30 text-white pr-10"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {buscandoEndereco && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
                  {!buscandoEndereco && enderecoEncontrado === true && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {!buscandoEndereco && enderecoEncontrado === false && (
                    <MapPin className="w-4 h-4 text-gray-500 cursor-pointer" onClick={() => setMostrarCoordsManual(!mostrarCoordsManual)} title="Inserir coordenadas" />
                  )}
                </div>
              </div>
              {coordenadasPreview && (
                <p className="text-[10px] text-gray-500 mt-1">
                  📍 {coordenadasPreview.lat.toFixed(4)}, {coordenadasPreview.lon.toFixed(4)}
                </p>
              )}
              {mostrarCoordsManual && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input
                    type="number"
                    step="0.000001"
                    value={formData.latitude || ''}
                    onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value) || undefined})}
                    placeholder="Latitude"
                    className="bg-protec-dark border-protec-gold/30 text-white text-sm h-8"
                  />
                  <Input
                    type="number"
                    step="0.000001"
                    value={formData.longitude || ''}
                    onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value) || undefined})}
                    placeholder="Longitude"
                    className="bg-protec-dark border-protec-gold/30 text-white text-sm h-8"
                  />
                </div>
              )}
            </div>

            {/* Seção de Grupo */}
            <div className="border-t border-gray-700 pt-4 mt-4">
              <Label className="flex items-center gap-2 mb-3 text-gray-300">
                <Layers className="w-4 h-4" />
                Grupo / Totem <span className="text-gray-500 text-xs">(opcional)</span>
              </Label>
              
              {/* Seletor de modo */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => { setGrupoMode('sem_grupo'); setSelectedGrupoId(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-colors ${
                    grupoMode === 'sem_grupo' 
                      ? 'bg-protec-gold/20 border-protec-gold text-protec-gold' 
                      : 'bg-protec-dark border-protec-gold/30 text-gray-400 hover:border-protec-gold/50'
                  }`}
                >
                  Sem grupo
                </button>
                <button
                  type="button"
                  onClick={() => setGrupoMode('existente')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-colors ${
                    grupoMode === 'existente' 
                      ? 'bg-protec-gold/20 border-protec-gold text-protec-gold' 
                      : 'bg-protec-dark border-protec-gold/30 text-gray-400 hover:border-protec-gold/50'
                  }`}
                  disabled={grupos.length === 0}
                >
                  Grupo existente
                </button>
                <button
                  type="button"
                  onClick={() => setGrupoMode('novo')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-colors flex items-center justify-center gap-1 ${
                    grupoMode === 'novo' 
                      ? 'bg-protec-gold/20 border-protec-gold text-protec-gold' 
                      : 'bg-protec-dark border-protec-gold/30 text-gray-400 hover:border-protec-gold/50'
                  }`}
                >
                  <FolderPlus className="w-3 h-3" />
                  Novo
                </button>
              </div>

              {/* Seletor de grupo existente */}
              {grupoMode === 'existente' && (
                <div className="bg-protec-dark/50 rounded-lg p-3">
                  <Label className="text-sm">Selecione o grupo</Label>
                  <select
                    value={selectedGrupoId || ''}
                    onChange={(e) => setSelectedGrupoId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full bg-protec-dark border border-protec-gold/30 text-white rounded-md px-3 py-2 mt-1"
                  >
                    <option value="">Selecionar grupo...</option>
                    {grupos.map(grupo => (
                      <option key={grupo.id} value={grupo.id}>
                        {grupo.nome} ({grupo.total_cameras} câmeras)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Formulário de novo grupo */}
              {grupoMode === 'novo' && (
                <div className="space-y-3 bg-protec-dark/50 rounded-lg p-3">
                  <div>
                    <Label className="text-sm">Nome do Grupo <span className="text-red-400">*</span></Label>
                    <Input
                      value={novoGrupo.nome}
                      onChange={(e) => setNovoGrupo({...novoGrupo, nome: e.target.value})}
                      placeholder="Ex: Totem Shopping Center"
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Endereço do grupo</Label>
                    <Input
                      value={novoGrupo.endereco}
                      onChange={(e) => setNovoGrupo({...novoGrupo, endereco: e.target.value})}
                      placeholder="Rua, número, bairro"
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Cidade</Label>
                      <Input
                        value={novoGrupo.cidade}
                        onChange={(e) => setNovoGrupo({...novoGrupo, cidade: e.target.value})}
                        placeholder="Cidade"
                        className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">UF</Label>
                      <Input
                        value={novoGrupo.estado}
                        onChange={(e) => setNovoGrupo({...novoGrupo, estado: e.target.value.toUpperCase().slice(0, 2)})}
                        placeholder="SC"
                        maxLength={2}
                        className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setModalOpen(false)}
              className="border-gray-500 text-gray-400"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={
                saving ||
                !formData.nome ||
                (formData.finalidade !== 'captura' && formData.finalidade !== 'visualizacao') ||
                (!formData.ip && !formData.serial_p2p && !formData.stream_id) ||
                (grupoMode === 'novo' && !novoGrupo.nome)
              }
              className="bg-protec-gold text-black hover:bg-protec-gold-light"
            >
              {saving ? 'Salvando...' : (editingCamera ? 'Atualizar' : 'Criar Câmera')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Tem certeza que deseja excluir a câmera <strong className="text-protec-gold">{cameraToDelete?.nome}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteModalOpen(false)}
              className="border-gray-500 text-gray-400"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Quem pode visualizar esta câmera */}
      <Dialog open={permissionsModalOpen} onOpenChange={setPermissionsModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-lg w-[calc(100vw-2rem)] sm:w-full overflow-hidden flex flex-col p-6 pr-10">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-protec-gold flex-shrink-0" />
              Acesso à câmera
            </DialogTitle>
            <CardDescription className="text-gray-400 break-words">
              Clientes que podem visualizar: <strong className="text-white">{permissionsCamera?.nome}</strong>
            </CardDescription>
          </DialogHeader>
          {loadingPermissions ? (
            <div className="py-6 text-center text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : (
            <div className="space-y-4 py-2 overflow-auto min-h-0">
              <div className="space-y-2">
                <Label className="text-gray-400">Adicionar cliente</Label>
                <div className="flex gap-2 min-w-0">
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value === '' ? '' : Number(e.target.value))}
                    className="min-w-0 flex-1 bg-protec-dark border border-protec-gold/30 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-protec-gold/50"
                  >
                    <option value="">Selecione um cliente</option>
                    {clientsList
                      .filter(c => !permissionsList.some(p => p.user_id === c.id))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.nome_completo || c.email} ({c.email})</option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    className="bg-protec-gold text-black hover:bg-protec-gold-light flex-shrink-0"
                    onClick={handleAddPermission}
                    disabled={!selectedClientId}
                  >
                    Adicionar
                  </Button>
                </div>
              </div>
              <div className="min-w-0">
                <Label className="text-gray-400">Com acesso ({permissionsList.length})</Label>
                {permissionsList.length === 0 ? (
                  <p className="text-gray-500 text-sm py-2">Nenhum cliente com permissão. Apenas o admin vê todas; conceda acesso acima.</p>
                ) : (
                  <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {permissionsList.map((p) => (
                      <li key={p.id} className="flex items-center gap-2 py-2 px-2 rounded bg-protec-dark/80 min-w-0">
                        <span className="text-sm text-white truncate min-w-0 flex-1">{p.user_nome} <span className="text-gray-400">({p.user_email})</span></span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0 flex-shrink-0"
                          onClick={() => handleRemovePermission(p.user_id)}
                          title="Remover acesso"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Preview da Câmera - Responsivo */}
      {viewModalOpen && viewingCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={() => setViewModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-4xl max-h-[95vh] bg-protec-dark border border-protec-gold/30 rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-2 sm:p-3 border-b border-protec-gold/20 bg-gradient-to-r from-protec-dark-light to-protec-dark">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${viewingCamera.status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <Camera className={`w-4 h-4 sm:w-5 sm:h-5 ${viewingCamera.status === 'online' ? 'text-green-400' : 'text-red-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-white font-bold text-sm sm:text-lg truncate pb-0.5 min-h-[1.25em]" style={{ paddingBottom: '2px' }} title={viewingCamera.nome}>{viewingCamera.nome}</h2>
                  <p className="text-gray-400 text-xs sm:text-sm truncate pb-0.5" style={{ paddingBottom: '2px' }}>
                    {viewingCamera.localizacao || 'Sem localização'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <Badge className={`${viewingCamera.status === 'online' ? 'bg-green-500' : 'bg-red-500'} text-white text-xs`}>
                  <span className="hidden sm:inline">{viewingCamera.status === 'online' ? '● Online' : '● Offline'}</span>
                  <span className="sm:hidden">●</span>
                </Badge>
                
                <button
                  className="text-gray-400 hover:text-white transition-colors text-lg sm:text-xl px-1 sm:px-2"
                  onClick={() => setViewModalOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Área de Vídeo — somente ao vivo */}
            <div className="bg-black relative aspect-video">
              <div className="relative w-full h-full overflow-hidden">
                {liveStreamName && <iframe
                  src={`${GO2RTC_URL}/stream.html?src=${liveStreamName}&mode=mse,hls,mjpeg&video`}
                  className="border-0 overflow-hidden absolute inset-0"
                  allow="autoplay"
                  scrolling="no"
                  muted
                  style={{
                    background: 'black',
                    width: 'calc(100% + 40px)',
                    height: 'calc(100% + 60px)',
                    marginTop: '-10px',
                    marginLeft: '-10px'
                  }}
                />}
                <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs flex items-center gap-1 sm:gap-1.5 shadow-lg z-10">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></span>
                  AO VIVO
                </div>
                {previewLine && (
                  <svg
                    className="absolute inset-0 z-20 pointer-events-none w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <line
                      x1={previewLine.x1 * 100}
                      y1={previewLine.y1 * 100}
                      x2={previewLine.x2 * 100}
                      y2={previewLine.y2 * 100}
                      stroke="rgb(251 191 36)"
                      strokeWidth="0.6"
                      strokeDasharray="2 1"
                    />
                  </svg>
                )}
                {previewZone && (
                  <>
                    <div
                      className="absolute z-20 rounded-sm border-2 border-yellow-400/95 bg-yellow-300/10 pointer-events-none shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
                      style={{
                        left: `${previewZone.x1 * 100}%`,
                        top: `${previewZone.y1 * 100}%`,
                        width: `${Math.max(0.5, (previewZone.x2 - previewZone.x1) * 100)}%`,
                        height: `${Math.max(0.5, (previewZone.y2 - previewZone.y1) * 100)}%`,
                      }}
                    />
                    <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 z-20 pointer-events-none rounded border border-yellow-400/50 bg-black/70 px-2 py-1 text-[10px] sm:text-xs text-yellow-200">
                      Zona ativa ({previewZone.rule})
                    </div>
                  </>
                )}
                </div>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraManagement;
